import {
  Box,
  Checkbox,
  Flex,
  Input,
  Menu,
  MenuButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Select,
  Text,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowDown, FiArrowUp, FiColumns, FiFilter } from 'react-icons/fi';

import { ActionMenuList } from 'components/ActionMenuList';
import FigmaSearchIcon from 'components/Icons/FigmaSearchIcon';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import FigmaFileDownloadIcon from 'components/Icons/FigmaFileDownloadIcon';
import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  controlHeight,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { fontSize, radius, shadow } from 'styles/revampTokens';

import colors from './dataTheme';
import {
  getDistinctValues,
  type FiltersState,
  type PreviewColumn,
  type SortState,
} from './dataPreviewUtils';
import { CountPill, controlStyle } from './detailUi';

const panelStyle = {
  w: '290px',
  bg: colors.surface,
  border: '1px solid',
  borderColor: colors.border,
  borderRadius: radius.overlay,
  boxShadow: shadow.overlay,
  _focusVisible: { outline: 'none', boxShadow: shadow.overlay },
} as const;

const linkButtonStyle = {
  ...PLAN_TEXT.labelSmMedium,
  color: colors.primary,
  cursor: 'pointer',
  _hover: { opacity: 0.8 },
} as const;

const ActiveDot = () => <Box w='6px' h='6px' borderRadius='full' bg={colors.primary} />;

interface FilterPanelProperties {
  columns: PreviewColumn[];
  rows: any[];
  filters: FiltersState;
  onApply: (filters: FiltersState) => void;
}

const FilterPanel = ({ columns, rows, filters, onApply }: FilterPanelProperties) => {
  const [draft, setDraft] = useState<FiltersState>(filters);
  const [column, setColumn] = useState(columns[0]?.current ?? '');
  const [valueSearch, setValueSearch] = useState('');

  useEffect(() => setDraft(filters), [filters]);

  const values = useMemo(() => getDistinctValues(rows, column), [rows, column]);
  const visibleValues = values.filter((value) =>
    value.toLowerCase().includes(valueSearch.trim().toLowerCase()),
  );
  const selected = draft[column] ?? [];

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    setDraft({ ...draft, [column]: next });
  };

  return (
    <Box p={3}>
      <Text {...PLAN_TEXT.labelSmMedium} color={colors.textSecondary} mb={1}>
        Column
      </Text>
      <Select
        value={column}
        onChange={(event) => {
          setColumn(event.target.value);
          setValueSearch('');
        }}
        h={controlHeight}
        fontSize={fontSize.body}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        borderColor={colors.border}
        _focusVisible={{ borderColor: colors.primary, boxShadow: 'none' }}
      >
        {columns.map((item) => (
          <option key={item.current} value={item.current}>
            {item.modified}
          </option>
        ))}
      </Select>

      <Flex alignItems='center' justifyContent='space-between' mt={3} mb={1}>
        <Text {...PLAN_TEXT.labelSmMedium} color={colors.textSecondary}>
          Values
        </Text>
        {selected.length > 0 && (
          <Text {...linkButtonStyle} onClick={() => setDraft({ ...draft, [column]: [] })}>
            Clear
          </Text>
        )}
      </Flex>
      <Input
        value={valueSearch}
        onChange={(event) => setValueSearch(event.target.value)}
        placeholder='Search values'
        h={controlHeight}
        fontSize={fontSize.body}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        borderColor={colors.border}
        _focusVisible={{ borderColor: colors.primary, boxShadow: 'none' }}
        mb={2}
      />
      <Box maxH='180px' overflowY='auto' className='scroll'>
        {visibleValues.length === 0 && (
          <Text {...PLAN_TEXT.body} color={colors.textSecondary} py={2}>
            No values found
          </Text>
        )}
        {visibleValues.map((value) => (
          <Flex key={value} alignItems='center' py='5px'>
            <Checkbox
              colorScheme='purple'
              isChecked={selected.includes(value)}
              onChange={() => toggle(value)}
              overflow='hidden'
            >
              <Text {...PLAN_TEXT.body} color={colors.textDark} noOfLines={1} maxW='210px'>
                {value}
              </Text>
            </Checkbox>
          </Flex>
        ))}
      </Box>

      <Flex gap={2} mt={3}>
        <Flex
          as='button'
          type='button'
          {...controlStyle}
          flex={1}
          justifyContent='center'
          onClick={() => {
            setDraft({});
            onApply({});
          }}
        >
          Clear all
        </Flex>
        <Flex
          as='button'
          type='button'
          flex={1}
          h={controlHeight}
          alignItems='center'
          justifyContent='center'
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          bg={colors.primary}
          color={colors.white}
          fontSize={fontSize.body}
          fontWeight={500}
          cursor='pointer'
          _hover={{ bg: colors.primaryHover }}
          onClick={() => onApply(draft)}
        >
          Apply
        </Flex>
      </Flex>
    </Box>
  );
};

interface ColumnsPanelProperties {
  columns: PreviewColumn[];
  hiddenColumns: Set<string>;
  onChange: (hidden: Set<string>) => void;
}

const ColumnsPanel = ({ columns, hiddenColumns, onChange }: ColumnsPanelProperties) => {
  const [query, setQuery] = useState('');
  const shown = columns.length - hiddenColumns.size;
  const visibleList = columns.filter((column) =>
    column.modified.toLowerCase().includes(query.trim().toLowerCase()),
  );

  const toggle = (key: string) => {
    const next = new Set(hiddenColumns);
    if (next.has(key)) next.delete(key);
    else if (columns.length - next.size > 1) next.add(key);
    onChange(next);
  };

  return (
    <Box>
      <Flex alignItems='center' justifyContent='space-between' px={3} pt={3} pb={2}>
        <Flex alignItems='center' gap={2}>
          <Text {...PLAN_TEXT.bodySemibold} color={colors.textDark}>
            Columns
          </Text>
          <CountPill>
            {shown}/{columns.length}
          </CountPill>
        </Flex>
        {hiddenColumns.size > 0 && (
          <Text {...linkButtonStyle} onClick={() => onChange(new Set())}>
            Show all
          </Text>
        )}
      </Flex>

      <Box px={3} pb={2}>
        <Flex
          alignItems='center'
          gap={2}
          px='10px'
          h={controlHeight}
          border='1px solid'
          borderColor={colors.border}
          borderRadius={PLAN_REVAMP_RADIUS_SM}
          _focusWithin={{ borderColor: colors.primary }}
        >
          <FigmaSearchIcon width={13} height={13} color={colors.textSecondary} />
          <Input
            variant='unstyled'
            placeholder='Search columns'
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            fontSize={fontSize.body}
            color={colors.textDark}
            _placeholder={{ color: colors.textSecondary }}
          />
        </Flex>
      </Box>

      <Box maxH='260px' overflowY='auto' className='scroll' px={2} pb={2}>
        {visibleList.length === 0 && (
          <Text {...PLAN_TEXT.body} color={colors.textSecondary} px={2} py={3}>
            No columns found
          </Text>
        )}
        {visibleList.map((column) => {
          const isShown = !hiddenColumns.has(column.current);
          return (
            <Flex
              key={column.current}
              alignItems='center'
              gap={3}
              px={2}
              h='32px'
              borderRadius='6px'
              cursor='pointer'
              _hover={{ bg: colors.hover }}
              onClick={() => toggle(column.current)}
            >
              <Checkbox
                colorScheme='purple'
                isChecked={isShown}
                pointerEvents='none'
                tabIndex={-1}
                onChange={() => {}}
              />
              <Text
                {...PLAN_TEXT.body}
                color={isShown ? colors.textDark : colors.textSecondary}
                noOfLines={1}
                title={column.modified}
              >
                {column.modified}
              </Text>
            </Flex>
          );
        })}
      </Box>
    </Box>
  );
};

interface PreviewToolbarProperties {
  columns: PreviewColumn[];
  rows: any[];
  search: string;
  onSearchChange: (value: string) => void;
  filters: FiltersState;
  onFiltersChange: (filters: FiltersState) => void;
  sort: SortState | null;
  onSortChange: (sort: SortState | null) => void;
  hiddenColumns: Set<string>;
  onHiddenColumnsChange: (hidden: Set<string>) => void;
  onDownload: (format: 'original' | 'csv') => void;
  originalLabel: string;
  csvBlockReason: string | null;
  isDownloading: boolean;
  canDownload: boolean;
}

const PreviewToolbar = ({
  columns,
  rows,
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  sort,
  onSortChange,
  hiddenColumns,
  onHiddenColumnsChange,
  onDownload,
  originalLabel,
  csvBlockReason,
  isDownloading,
  canDownload,
}: PreviewToolbarProperties) => {
  const activeFilterCount = Object.values(filters).filter((values) => values.length > 0).length;
  const hiddenCount = hiddenColumns.size;
  const hasColumns = columns.length > 0;

  return (
    <Flex alignItems='center' gap={2} flexWrap='wrap'>
      <Flex
        alignItems='center'
        gap={2}
        px='10px'
        flex='0 1 280px'
        minW='180px'
        h={controlHeight}
        border='1px solid'
        borderColor={colors.border}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        bg={colors.surface}
        _focusWithin={{ borderColor: colors.primary }}
      >
        <FigmaSearchIcon width={13} height={13} color={colors.textSecondary} />
        <Input
          variant='unstyled'
          placeholder='Search data...'
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          fontSize={fontSize.body}
          color={colors.textDark}
          _placeholder={{ color: colors.textSecondary }}
        />
      </Flex>

      <Box flex={1} />

      <Popover placement='bottom-end' isLazy>
        <PopoverTrigger>
          <Flex as='button' type='button' {...controlStyle} opacity={hasColumns ? 1 : 0.5}>
            <FiFilter size={13} strokeWidth={2.6} color={colors.textSecondary} />
            Filter
            {activeFilterCount > 0 && <ActiveDot />}
          </Flex>
        </PopoverTrigger>
        <PopoverContent {...panelStyle}>
          <FilterPanel columns={columns} rows={rows} filters={filters} onApply={onFiltersChange} />
        </PopoverContent>
      </Popover>

      <Popover placement='bottom-end' isLazy>
        <PopoverTrigger>
          <Flex as='button' type='button' {...controlStyle} opacity={hasColumns ? 1 : 0.5}>
            {sort?.direction === 'desc' ? (
              <FiArrowDown size={13} strokeWidth={2.6} color={colors.textSecondary} />
            ) : (
              <FiArrowUp size={13} strokeWidth={2.6} color={colors.textSecondary} />
            )}
            Sort
            {sort && <ActiveDot />}
          </Flex>
        </PopoverTrigger>
        <PopoverContent {...panelStyle}>
          <PopoverBody p={3}>
            <Text {...PLAN_TEXT.labelSmMedium} color={colors.textSecondary} mb={1}>
              Column
            </Text>
            <Select
              value={sort?.column ?? ''}
              placeholder='Select column'
              onChange={(event) =>
                onSortChange(
                  event.target.value
                    ? { column: event.target.value, direction: sort?.direction ?? 'asc' }
                    : null,
                )
              }
              h={controlHeight}
              fontSize={fontSize.body}
              borderRadius={PLAN_REVAMP_RADIUS_SM}
              borderColor={colors.border}
              _focusVisible={{ borderColor: colors.primary, boxShadow: 'none' }}
            >
              {columns.map((item) => (
                <option key={item.current} value={item.current}>
                  {item.modified}
                </option>
              ))}
            </Select>
            <Flex gap={2} mt={3}>
              {(['asc', 'desc'] as const).map((direction) => {
                const isActive = sort?.direction === direction;
                return (
                  <Flex
                    key={direction}
                    as='button'
                    type='button'
                    flex={1}
                    h={controlHeight}
                    alignItems='center'
                    justifyContent='center'
                    gap='6px'
                    border='1px solid'
                    borderColor={isActive ? colors.primary : colors.border}
                    borderRadius={PLAN_REVAMP_RADIUS_SM}
                    bg={isActive ? colors.activeBg : colors.surface}
                    color={isActive ? colors.primary : colors.textDark}
                    fontSize={fontSize.body}
                    fontWeight={500}
                    cursor={sort ? 'pointer' : 'not-allowed'}
                    opacity={sort ? 1 : 0.5}
                    onClick={() => sort && onSortChange({ ...sort, direction })}
                  >
                    {direction === 'asc' ? <FiArrowUp size={13} /> : <FiArrowDown size={13} />}
                    {direction === 'asc' ? 'Ascending' : 'Descending'}
                  </Flex>
                );
              })}
            </Flex>
            {sort && (
              <Text {...linkButtonStyle} mt={3} onClick={() => onSortChange(null)}>
                Clear sort
              </Text>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <Popover placement='bottom-end' isLazy>
        <PopoverTrigger>
          <Flex as='button' type='button' {...controlStyle} opacity={hasColumns ? 1 : 0.5}>
            <FiColumns size={13} strokeWidth={2.6} color={colors.textSecondary} />
            Columns
            {hiddenCount > 0 && <ActiveDot />}
          </Flex>
        </PopoverTrigger>
        <PopoverContent {...panelStyle} w='280px'>
          <ColumnsPanel
            columns={columns}
            hiddenColumns={hiddenColumns}
            onChange={onHiddenColumnsChange}
          />
        </PopoverContent>
      </Popover>

      <Menu placement='bottom-end' strategy='fixed'>
        <MenuButton {...controlStyle} opacity={canDownload ? 1 : 0.5}>
          <Flex alignItems='center' gap='6px'>
            <FigmaFileDownloadIcon width={14} height={14} color={colors.textSecondary} />
            Download
            <FigmaTriangleDown width={8} height={4} color={colors.textSecondary} />
          </Flex>
        </MenuButton>
        <ActionMenuList
          minW='220px'
          items={[
            {
              key: 'original',
              label: originalLabel,
              icon: <FigmaFileDownloadIcon width={14} height={14} color={colors.textDark} />,
              isDisabled: !canDownload || isDownloading,
              onSelect: () => onDownload('original'),
            },
            {
              key: 'csv',
              label: 'Download as CSV',
              icon: <FigmaFileDownloadIcon width={14} height={14} color={colors.textDark} />,
              isDisabled: !canDownload || isDownloading || Boolean(csvBlockReason),
              tooltip: csvBlockReason ?? undefined,
              onSelect: () => onDownload('csv'),
            },
          ]}
        />
      </Menu>
    </Flex>
  );
};

export default PreviewToolbar;
